import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import SectionHeading from "@/components/base/SectionHeading";
import useScrollReveal from "@/hooks/useScrollReveal";
import CareHero from "@/components/feature/care/CareHero";
import CareIntro from "@/components/feature/care/CareIntro";
import CarePanels from "@/components/feature/care/CarePanels";
import CareAnatomy from "@/components/feature/care/CareAnatomy";
import CareSymptoms from "@/components/feature/care/CareSymptoms";
import CareSteps from "@/components/feature/care/CareSteps";
import CareDoctor from "@/components/feature/care/CareDoctor";
import CareFaq from "@/components/feature/care/CareFaq";
import CareCta from "@/components/feature/care/CareCta";
import { doctors } from "@/data/doctors";
import type { Faq } from "@/types/content";

const careAreas = [
  {
    title: "Back Pain",
    text: "Persistent or recurring lower back pain that may relate to muscles, discs, joints or nerves. Most episodes settle with the right guidance, but lasting pain deserves a specialist opinion.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20lumbar%20spine%20with%20the%20lower%20back%20region%20highlighted%20on%20a%20clean%20light%20background%2C%20soft%20cyan%20and%20teal%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-spine-area-back-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of the lumbar spine, illustrating back pain assessment in Trichy",
  },
  {
    title: "Neck Pain",
    text: "Neck pain and stiffness that may follow posture, wear in the cervical spine or an injury. When pain travels into the arm or hand, it should be evaluated promptly.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20cervical%20spine%20with%20the%20neck%20region%20highlighted%20on%20a%20clean%20light%20background%2C%20soft%20cyan%20and%20teal%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-spine-area-neck-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of the cervical spine, illustrating neck pain evaluation",
  },
  {
    title: "Disc Problems",
    text: "Cervical and lumbar disc prolapse that can press on a nerve and cause pain, numbness or weakness in an arm or leg. Many disc problems improve without surgery.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20anatomical%20model%20of%20a%20spinal%20disc%20resting%20on%20a%20clean%20light%20desk%20in%20a%20modern%20clinic%2C%20soft%20neutral%20background%2C%20medical%20education%20photography%20with%20crisp%20detail%20and%20warm%20studio%20lighting&width=800&height=600&seq=ospc-spine-area-disc-2026&orientation=landscape",
    imageAlt: "Anatomical spinal disc model used to explain disc problems",
  },
  {
    title: "Sciatica & Nerve Pain",
    text: "Shooting pain, tingling or numbness that travels from the back or hip down into the leg. Assessment helps identify which nerve is involved and the best route to relief.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20medical%20illustration%20of%20the%20sciatic%20nerve%20pathway%20running%20from%20the%20lower%20spine%20through%20the%20hip%20and%20down%20the%20leg%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20medical%20education%20visual%2C%20no%20people&width=800&height=600&seq=ospc-spine-area-sciatica-2026&orientation=landscape",
    imageAlt: "Anatomical illustration of the sciatic nerve, illustrating sciatica and nerve pain",
  },
  {
    title: "Spinal Stenosis",
    text: "Narrowing of the space around the spinal cord or nerves, often causing heaviness in the legs when walking that eases with rest. Care ranges from physiotherapy to decompression surgery.",
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20medical%20illustration%20of%20a%20narrowed%20spinal%20canal%20with%20highlighted%20vertebrae%20on%20a%20soft%20neutral%20background%2C%20clean%20clinical%20education%20visual%20with%20calm%20teal%20and%20navy%20tones&width=800&height=600&seq=ospc-spine-area-stenosis-2026&orientation=landscape",
    imageAlt: "Medical illustration of a narrowed spinal canal in spinal stenosis",
  },
];

const anatomyParts = [
  {
    icon: "ri-stack-line",
    title: "Vertebrae",
    text: "The bones that stack to form your spinal column and protect the spinal cord and nerves.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Discs",
    text: "Soft cushions between the vertebrae that absorb shock and allow the spine to bend and twist.",
  },
  {
    icon: "ri-share-line",
    title: "Spinal cord & nerves",
    text: "The network that carries signals between your brain and the rest of your body.",
  },
  {
    icon: "ri-links-line",
    title: "Joints & muscles",
    text: "The small joints and muscles that guide movement and support the spine.",
  },
];

const symptoms = [
  { icon: "ri-body-scan-line", title: "Persistent back pain" },
  { icon: "ri-user-line", title: "Neck pain or stiffness" },
  { icon: "ri-share-forward-line", title: "Pain into arms or legs" },
  { icon: "ri-hand-heart-line", title: "Numbness or tingling" },
  { icon: "ri-heart-pulse-line", title: "Weakness in an arm or leg" },
  { icon: "ri-walk-line", title: "Difficulty with movement" },
  { icon: "ri-alert-line", title: "Loss of bladder or bowel control" },
  { icon: "ri-error-warning-line", title: "Pain after an injury" },
];

const diagnosisSteps = [
  {
    step: "01",
    title: "Consultation",
    text: "We listen carefully to your symptoms, history and concerns before anything else.",
    icon: "ri-chat-3-line",
  },
  {
    step: "02",
    title: "Clinical Evaluation",
    text: "A hands-on examination of movement, strength and nerve function.",
    icon: "ri-stethoscope-line",
  },
  {
    step: "03",
    title: "Diagnostic Assessment",
    text: "Imaging such as X-ray or MRI when it is needed to confirm the cause.",
    icon: "ri-scan-2-line",
  },
  {
    step: "04",
    title: "Personalised Care Plan",
    text: "A clear plan built around you, considering non-surgical options first.",
    icon: "ri-clipboard-line",
  },
];

const carePlan = [
  {
    icon: "ri-heart-2-line",
    title: "Non-surgical care first",
    text: "Activity guidance, medication and posture advice are considered before any surgery.",
    image:
      "https://readdy.ai/api/search-image?query=Physiotherapy%20equipment%20including%20resistance%20bands%20an%20exercise%20mat%20and%20a%20foam%20roller%20arranged%20neatly%20in%20a%20bright%20modern%20clinic%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=800&height=600&seq=ospc-spine-plan-nonsurgical-2026&orientation=landscape",
    imageAlt: "Physiotherapy equipment used for non-surgical spine care",
  },
  {
    icon: "ri-run-line",
    title: "Rehabilitation support",
    text: "Structured physiotherapy and follow-up to help you return to daily activity safely.",
    image:
      "https://readdy.ai/api/search-image?query=Bright%20modern%20rehabilitation%20room%20with%20parallel%20bars%20and%20exercise%20equipment%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=800&height=600&seq=ospc-spine-plan-rehab-2026&orientation=landscape",
    imageAlt: "Rehabilitation equipment in a bright recovery room for spine care",
  },
  {
    icon: "ri-microscope-line",
    title: "Minimally invasive surgery",
    text: "Where surgery is needed, techniques that aim to reduce tissue damage and aid recovery.",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20operating%20theatre%20prepared%20for%20a%20minimally%20invasive%20spine%20procedure%20with%20clean%20equipment%20and%20soft%20cool%20lighting%2C%20premium%20healthcare%20interior%20photography%2C%20calm%20and%20precise%20atmosphere&width=800&height=600&seq=ospc-spine-plan-mininvasive-2026&orientation=landscape",
    imageAlt: "Modern operating theatre prepared for minimally invasive spine surgery",
  },
  {
    icon: "ri-refresh-line",
    title: "Complex & revision surgery",
    text: "Senior-level experience for complex and revision spine cases that need specialist care.",
    image:
      "https://readdy.ai/api/search-image?query=Spine%20MRI%20scan%20films%20on%20a%20digital%20display%20in%20a%20modern%20quiet%20reading%20room%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=800&height=600&seq=ospc-spine-plan-revision-2026&orientation=landscape",
    imageAlt: "Spine MRI scans reviewed while planning revision spine surgery",
  },
];

const spineFaqs: Faq[] = [
  {
    q: "When should I consult a spine specialist?",
    a: "Consider a specialist opinion if neck or back pain lasts more than a few weeks, keeps returning, limits your daily activities, travels into an arm or leg, or comes with numbness or weakness. Symptoms such as loss of bladder or bowel control need urgent attention.",
  },
  {
    q: "What can cause persistent back pain?",
    a: "Common causes include muscle strain and posture, disc problems, spinal stenosis, arthritis of the spine and previous injury. Most cases improve with conservative care, but lasting or severe pain should be assessed to find the underlying cause.",
  },
  {
    q: "When should neck pain be evaluated?",
    a: "Neck pain that persists, follows an injury, or is accompanied by pain, numbness or weakness in the arm or hand deserves a specialist assessment. Most simple neck pain improves with posture care and physiotherapy.",
  },
  {
    q: "What happens during a spine consultation?",
    a: "Your specialist will discuss your symptoms and history, examine your movement, strength and nerve function, and review any scans. Where needed, further imaging may be arranged before a clear plan is discussed with you.",
  },
  {
    q: "How can I book an appointment?",
    a: "You can request an appointment through the booking form on this website, or call the clinic on +91 99433 16050. Our team will confirm a suitable time with you.",
  },
];

export default function SpineCare() {
  useScrollReveal();

  return (
    <Layout>
      <Seo
        title="Spine Care in Trichy | Spine Specialist & Spine Surgery"
        description="Specialist spine care in Srirangam, Trichy for back pain, neck pain, disc problems and spinal stenosis, including minimally invasive and revision spine surgery."
        path="/services/spine-care"
      />
      <JsonLd
        id="ld-spine"
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalWebPage",
              name: "Spine Care",
              url: "https://orthospine.care/services/spine-care",
              about: { "@type": "MedicalCondition", name: "Spine disorders" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://orthospine.care/" },
                { "@type": "ListItem", position: 2, name: "Services", item: "https://orthospine.care/services" },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Spine Care",
                  item: "https://orthospine.care/services/spine-care",
                },
              ],
            },
          ],
        }}
      />

      <CareHero
        eyebrow="Spine Care"
        title={
          <>
            Advanced Care for Your
            <span className="italic text-primary-700"> Spine</span>
          </>
        }
        intro="Focused, senior-level care for neck and back conditions — from accurate diagnosis to minimally invasive and complex spine surgery, at Ortho Spine Care in Srirangam, Trichy."
        image={doctors[0].image}
        imageAlt={doctors[0].imageAlt}
        badgeIcon="ri-body-scan-line"
        badgeTitle="Spine Surgery"
        badgeText="Minimally invasive & revision"
        chips={["Back Pain", "Neck Pain", "Disc Problems", "Sciatica", "Spinal Stenosis", "Spine Trauma"]}
        chipsLabel="Spine concerns we see"
        crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: "Spine Care" }]}
      />

      <CareIntro
        eyebrow="Spine care at Ortho Spine Care"
        title="Specialist Care for Your Spine"
        paragraphs={[
          "Spine problems can affect movement, sleep and confidence. Our spine care begins with a careful clinical assessment and appropriate imaging to identify the exact cause of your symptoms, not just the site of the pain.",
          "Wherever possible we consider non-surgical treatment first, including physiotherapy and medication. For cases that need surgery, we offer minimally invasive techniques and, where required, complex and revision spine surgery.",
        ]}
        points={[
          "Accurate diagnosis before any treatment decision",
          "Non-surgical options considered first wherever suitable",
          "Minimally invasive and revision spine surgery experience",
        ]}
        image="https://readdy.ai/api/search-image?query=Detailed%20anatomical%20spine%20model%20and%20spinal%20X%20ray%20films%20arranged%20on%20a%20clean%20consultation%20desk%20in%20a%20bright%20modern%20clinic%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=1200&height=800&seq=ospc-spine-intro-2026&orientation=landscape"
        imageAlt="Anatomical spine model and spinal X-ray films used to explain spine care in Trichy"
        ctaLabel="Learn More About Spine Care"
        ctaTo="/services/spine-disorders"
      />

      <CarePanels
        eyebrow="Spine care areas"
        title="The spine concerns we look after"
        description="Each area is assessed individually. Understanding the cause is the first step towards the right care."
        panels={careAreas}
        defaultTo="/services/spine-disorders"
        tone="tinted"
      />

      <CareAnatomy
        eyebrow="Patient education"
        title="Understanding Your Spine"
        description="Your spine is a remarkable structure. Knowing how it is put together makes it easier to understand what may be causing your symptoms."
        image="https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20illustration%20of%20the%20human%20spine%20and%20vertebrae%20on%20a%20soft%20neutral%20background%2C%20clean%20medical%20education%20visual%20with%20calm%20teal%20and%20navy%20tones%20and%20subtle%20depth&width=900&height=1100&seq=ospc-spine-anatomy-2026&orientation=portrait"
        imageAlt="Anatomical illustration of the human spine and vertebrae"
        overlayTitle="A protected structure"
        overlayText="The spine supports the body, allows movement and shields the nerves that carry signals throughout the body."
        parts={anatomyParts}
        tone="light"
      />

      <CareSymptoms
        eyebrow="When to seek care"
        title="When Should You Seek Spine Care?"
        description="Many episodes of back or neck pain settle on their own. The signs below are a good reason to arrange a specialist assessment, so the cause can be understood early."
        items={symptoms}
        image="https://readdy.ai/api/search-image?query=Anatomical%20spine%20model%20and%20a%20posture%20chart%20on%20a%20clean%20desk%20in%20a%20bright%20modern%20clinic%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=900&height=1200&seq=ospc-spine-symptoms-2026&orientation=portrait"
        imageAlt="Spine model and posture chart used during a clinical assessment"
        ctaLabel="Consult a Specialist"
        tone="tinted"
      />

      <CareSteps
        eyebrow="Diagnosis"
        title="Understanding the Cause"
        description="A clear diagnosis comes before any treatment decision. Here is how we work through your spine concern, step by step."
        steps={diagnosisSteps}
        tone="light"
      />

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Our approach"
            title="A Care Plan Designed Around You"
            description="Care is tailored to your condition and circumstances. These are the options we consider, always starting with the least invasive that is appropriate for you."
          />
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {carePlan.map((item) => (
              <article
                key={item.title}
                className="reveal flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50"
              >
                <div className="h-40 w-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    title={item.imageAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-700 ease-out hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CareDoctor
        eyebrow="Meet your specialist"
        title="Meet Your Spine Specialist"
        ctaLabel="Book a Consultation"
      />

      <CareFaq eyebrow="Spine care FAQ" title="Spine care questions, answered" items={spineFaqs} idPrefix="spine" tone="light" />

      <CareCta
        title="Do not let spine pain hold you back"
        text="Request a consultation with our spine specialist to understand the cause of your symptoms and the care options available to you."
        image="https://readdy.ai/api/search-image?query=Modern%20bright%20orthopaedic%20clinic%20reception%20with%20soft%20warm%20natural%20light%20and%20neutral%20tones%2C%20premium%20healthcare%20architectural%20photography%2C%20calm%20clean%20and%20inviting%20atmosphere&width=1600&height=900&seq=ospc-spine-cta-2026&orientation=landscape"
      />
    </Layout>
  );
}